import { Injectable, inject, signal, effect, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from './auth.service';
import { MessageService } from '@openng/optimus-ui/api';
import { environment } from '../../../environments/environment';
import { switchMap, timer, Subscription, of } from 'rxjs';
import { catchError, map, tap, filter } from 'rxjs/operators';
import { ImportStatusResponse, WebsiteImportProgress } from '../models/website-import.interface';
import { OnboardingFailureCode } from '../models/auth.interface';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserRole } from '../models/user-role.types';

@Injectable({ providedIn: 'root' })
export class OnboardingOrchestratorService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly apiUrl = environment.apiUrl;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  private readonly internalWaitScreenVisible = signal(false);

  /**
   * Shown over whatever the user is on, the knowledge base included: it is where the stages of the
   * run are named. Closing it leaves the same progress reported in place, on the knowledge base
   * screen and on the card in the corner.
   */
  readonly isWaitScreenVisible = computed(() => this.internalWaitScreenVisible() && !this.isBillingFlow());

  /**
   * Picking a plan is its own decision, taken on a screen the crawl has nothing to do with. Neither
   * the stage overlay nor the corner card belongs on top of it.
   */
  private readonly isBillingFlow = computed(() => {
    const url = this.currentUrl() || '';
    return url.includes('subscription-') || url.includes('/pricing') || url.includes('/checkout');
  });

  readonly progress = signal<WebsiteImportProgress | null>(null);

  /** A retry request in flight, so the dialog's button cannot queue a second one. */
  readonly retrying = signal(false);
  private pollSub?: Subscription;

  private readonly backgroundProgressDismissed = signal(false);
  private readonly backgroundFailureDismissed = signal(false);

  private readonly isEligibleUser = computed(() => {
    const role = this.auth.currentUser()?.role;
    return role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN;
  });

  readonly onboardingStatus = computed(() => this.auth.currentUser()?.organisation?.onboardingStatus ?? 'NONE');

  /**
   * What the failure screen leads with. Says what we did, not what we could not do — and where the
   * address was never in doubt, says the site may not be open to us rather than blaming it.
   */
  readonly failureHeadline = computed(() => {
    const code = this.auth.currentUser()?.organisation?.onboardingFailureCode;
    const domain = this.domain();
    if (code === 'NO_CONTENT') return `We opened ${domain} but found no pages to read`;
    if (code === 'DNS_NOT_FOUND' || code === 'INVALID_URL') return `We could not find ${domain}`;
    return `${domain} may not be open for analysis`;
  });
  readonly domain = computed(() => this.auth.currentUser()?.organisation?.domain || 'your website');

  /**
   * A running import is reported wherever the user is standing — the knowledge base screen shows it
   * in place, and this card carries it everywhere else — until they dismiss it. Nothing waits for
   * the user to have watched the job: a reload, an invited member, a second tab all still see it.
   */
  readonly isBackgroundProgressVisible = computed(
    () => this.onboardingStatus() === 'PENDING' && !this.backgroundProgressDismissed() && !this.isWaitScreenVisible() && !this.isBillingFlow()
  );

  /**
   * The import gave up while nobody was watching — they left it running, or came back to a new
   * page altogether. The run itself is server-side and its verdict is on the organisation, so this
   * is what carries it: the dialog that asks for a corrected address is a click away rather than
   * something only the one session that watched the job ever saw.
   */
  readonly isBackgroundFailureVisible = computed(
    () => this.onboardingStatus() === 'FAILED' && this.isEligibleUser() && !this.backgroundFailureDismissed() && !this.isWaitScreenVisible()
  );

  private readonly phase = computed(() => this.progress()?.onboardingPhase || 'crawling');
  private readonly pagesRead = computed(() => this.progress()?.crawled || 0);
  private readonly pagesDiscovered = computed(() => Math.max(this.progress()?.discovered || 0, this.pagesRead()));

  readonly crawlState = computed(() => {
    if (this.onboardingStatus() === 'READY') return 'done';
    return this.phase() === 'crawling' ? 'in progress' : 'done';
  });

  readonly discoveryState = computed(() => {
    if (this.onboardingStatus() === 'READY') return 'done';
    if (this.phase() === 'crawling') return 'waiting';
    return this.phase() === 'discovering' ? 'in progress' : 'done';
  });

  readonly generationState = computed(() => {
    if (this.onboardingStatus() === 'READY') return 'done';
    if (this.phase() === 'generating') return 'in progress';
    return 'waiting';
  });

  readonly progressPercent = computed(() => {
    if (this.onboardingStatus() === 'READY') return 100;
    // 'canceled' from the API lands here as FAILED; stale progress must not read as live.
    if (this.onboardingStatus() === 'FAILED') return 0;
    const totalSteps = 3;
    let completed = 0;
    let currentProgress = 0;

    if (this.crawlState() === 'done') completed++;
    else if (this.crawlState() === 'in progress') {
      const discovered = Math.max(this.pagesDiscovered(), 1);
      currentProgress = Math.min((this.pagesRead() / discovered) * 0.9, 0.9);
    }

    if (this.discoveryState() === 'done') completed++;
    else if (this.discoveryState() === 'in progress') currentProgress = 0.5;

    if (this.generationState() === 'done') completed++;
    else if (this.generationState() === 'in progress') currentProgress = 0.5;

    return Math.round(((completed + currentProgress) / totalSteps) * 100);
  });

  readonly progressCaption = computed(() => {
    if (this.onboardingStatus() === 'READY') return 'Wrapping up...';
    if (this.crawlState() === 'in progress') return 'Reading your website pages...';
    if (this.discoveryState() === 'in progress') return 'Researching company context...';
    if (this.generationState() === 'in progress') return 'Writing playbook and ICP...';
    return 'Analyzing your website with AI...';
  });

  private lastUserId: number | null = null;
  private lastOrgId: number | null = null;
  private activelyWatchedOrgs = new Set<number>();
  private dismissedOrgs = new Set<number>();
  private toastDeliveredOrgs = new Set<number>();
  private hasSeenActiveJob = false;
  private consecutiveErrors = 0;

  constructor() {
    effect(() => {
      const user = this.auth.currentUser();
      if (!user) {
        this.stopPolling();
        this.internalWaitScreenVisible.set(false);
        this.lastUserId = null;
        this.lastOrgId = null;
        this.activelyWatchedOrgs.clear();
        this.dismissedOrgs.clear();
        this.toastDeliveredOrgs.clear();
        this.hasSeenActiveJob = false;
        this.consecutiveErrors = 0;
        this.backgroundProgressDismissed.set(false);
        this.backgroundFailureDismissed.set(false);
        return;
      }

      if (user.id !== this.lastUserId || user.organisationId !== this.lastOrgId) {
        this.lastUserId = user.id;
        this.lastOrgId = user.organisationId ?? null;
        this.activelyWatchedOrgs.clear();
        this.dismissedOrgs.clear();
        this.toastDeliveredOrgs.clear();
        this.hasSeenActiveJob = false;
        this.consecutiveErrors = 0;
        this.backgroundProgressDismissed.set(false);
        this.backgroundFailureDismissed.set(false);
      }

      const org = user.organisation;
      const orgId = user.organisationId;

      if (!org || !orgId) return;

      const status = org.onboardingStatus;

      if (status === 'PENDING') {
        this.startPolling(orgId, this.isEligibleUser());
      } else if (status === 'READY' || status === 'FAILED') {
        this.stopPolling();
        // A finished import is never worth a screen, however the status arrived — a refresh of the
        // user can land READY on an overlay that polling never got to close, and the card has
        // nothing to say about a workspace that is ready.
        if (status === 'READY' || !this.activelyWatchedOrgs.has(orgId) || this.dismissedOrgs.has(orgId)) {
          this.internalWaitScreenVisible.set(false);
        }
      }
    });
  }

  private startPolling(orgId: number, showOverlayForUser: boolean): void {
    if (this.pollSub) return;

    this.pollSub = timer(0, 3000)
      .pipe(
        switchMap(() => {
          const jobId = this.auth.currentUser()?.organisation?.onboardingJobId;

          if (!jobId) {
            return this.http
              .get<{ onboardingJobId: string | null; onboardingStatus: string; domain: string | null }>(`${this.apiUrl}/organisation/${orgId}`)
              .pipe(
                tap(fetchedOrg => {
                  const organisation = this.auth.currentUser()?.organisation;
                  if (!organisation) return;
                  // Every tick lands here, and a new user object restarts every computed that reads
                  // it; write only when this poll actually learned something.
                  const learnedDomain = !!fetchedOrg.domain && fetchedOrg.domain !== organisation.domain;
                  if (!fetchedOrg.onboardingJobId && !learnedDomain) return;

                  this.auth.currentUser.update(user => {
                    if (!user || !user.organisation) return user;
                    return {
                      ...user,
                      organisation: {
                        ...user.organisation,
                        // A retry sends the address as typed and the server stores it normalised;
                        // this is where the optimistic local copy is reconciled with it. Outside the
                        // job-id branch because the pill and the failure headline read this whether
                        // or not a job was queued.
                        domain: fetchedOrg.domain ?? user.organisation.domain,
                        ...(fetchedOrg.onboardingJobId && {
                          onboardingJobId: fetchedOrg.onboardingJobId,
                          onboardingStatus: fetchedOrg.onboardingStatus as 'NONE' | 'PENDING' | 'READY' | 'FAILED',
                        }),
                      },
                    };
                  });
                }),
                map(fetchedOrg => {
                  if (fetchedOrg.onboardingStatus === 'READY') return { status: 'completed' } as ImportStatusResponse;
                  if (fetchedOrg.onboardingStatus === 'FAILED') return { status: 'failed' } as ImportStatusResponse;
                  return {
                    status: 'queued',
                    progress: { onboardingPhase: 'crawling', discovered: 0, crawled: 0, imported: 0, skipped: 0, failed: 0 },
                  } as ImportStatusResponse;
                }),
                catchError(() => {
                  this.consecutiveErrors++;
                  if (this.consecutiveErrors >= 5) {
                    return of({ status: 'failed' } as ImportStatusResponse);
                  }
                  return of({
                    status: 'queued',
                    progress: { onboardingPhase: 'crawling', discovered: 0, crawled: 0, imported: 0, skipped: 0, failed: 0 },
                  } as ImportStatusResponse);
                })
              );
          }

          return this.http.get<ImportStatusResponse>(`${this.apiUrl}/documents/import-website/${jobId}`).pipe(
            catchError(() => {
              this.consecutiveErrors++;
              if (this.consecutiveErrors >= 5) {
                return of({ status: 'failed' } as ImportStatusResponse);
              }
              return of(null);
            })
          );
        })
      )
      .subscribe(res => {
        if (!res) return;
        this.consecutiveErrors = 0;

        if (res.progress) {
          this.progress.set(res.progress);
        }

        if (res.status === 'completed') {
          this.stopPolling();
          this.resetBackgroundProgress();
          this.updateLocalOrgStatus('READY');
          // A finished import is not worth a screen of its own: the hand-off announces it — the
          // knowledge base flies into the settings gear and the mascot says where it went.
          this.internalWaitScreenVisible.set(false);
        } else if (res.status === 'failed' || res.status === 'canceled') {
          this.stopPolling();
          this.resetBackgroundProgress();
          this.updateLocalOrgStatus('FAILED');
          // The job's own status says nothing about why. The failure dialog branches on the code,
          // so without this the run the user is watching lands on the generic screen and only a
          // reload turns it into the one that asks for the address.
          this.refreshFailureCode(orgId);

          if (this.hasSeenActiveJob && !this.toastDeliveredOrgs.has(orgId)) {
            this.toastDeliveredOrgs.add(orgId);
            if (!this.internalWaitScreenVisible()) {
              this.messageService.add({
                key: 'onboarding',
                severity: 'error',
                summary: 'Generation failed',
                detail: 'We encountered an error parsing your website.',
              });
            }
          }
        } else {
          this.hasSeenActiveJob = true;
          this.activelyWatchedOrgs.add(orgId);
          if (showOverlayForUser && !this.dismissedOrgs.has(orgId)) {
            this.internalWaitScreenVisible.set(true);
          }
        }
      });
  }

  private resetBackgroundProgress(): void {
    this.backgroundProgressDismissed.set(false);
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  skip(): void {
    const user = this.auth.currentUser();
    if (user?.organisationId) this.dismissedOrgs.add(user.organisationId);
    this.backgroundProgressDismissed.set(false);
    this.internalWaitScreenVisible.set(false);
  }

  /**
   * Runs the crawl again after the queue's own attempts ran out. The organisation goes back to
   * PENDING locally so the effect above restarts polling and the wait screen shows progress
   * instead of the failure it was showing a moment ago.
   */
  retryImport(website?: string): void {
    if (this.retrying()) return;
    const orgId = this.auth.currentUser()?.organisationId;
    if (!orgId) return;

    this.retrying.set(true);
    const body = website?.trim() ? { website: website.trim() } : {};
    this.http.post(`${this.apiUrl}/documents/import-website/retry-onboarding`, body).subscribe({
      next: () => {
        this.retrying.set(false);
        this.dismissedOrgs.delete(orgId);
        this.toastDeliveredOrgs.delete(orgId);
        this.hasSeenActiveJob = false;
        this.consecutiveErrors = 0;
        this.backgroundFailureDismissed.set(false);
        this.progress.set(null);
        this.auth.currentUser.update(user => {
          if (!user?.organisation) return user;
          return {
            ...user,
            organisation: {
              ...user.organisation,
              onboardingJobId: null,
              onboardingStatus: 'PENDING',
              onboardingFailureCode: null,
              ...(website?.trim() && { domain: website.trim() }),
            },
          };
        });
        this.internalWaitScreenVisible.set(true);
        // Same reason as in updateLocalOrgStatus: the cached user still says FAILED, and the next
        // navigation would put that verdict back on top of the run we just started.
        this.auth.invalidateUserCache();
      },
      error: (response: HttpErrorResponse) => {
        this.retrying.set(false);
        // A rejected address is about what was typed, a conflict is about a run that is already
        // going, and anything else is about us.
        const reason =
          response.status === 400
            ? { summary: 'That address is not a website we can read', detail: 'Check it and try again.' }
            : response.status === 409
              ? { summary: 'An import is already running', detail: 'Wait for it to finish, then try the new address.' }
              : { summary: 'Could not start the import', detail: 'Please try again in a moment.' };

        this.messageService.add({ key: 'onboarding', severity: 'error', ...reason });
      },
    });
  }

  /** Hides the notification-area progress card; the job itself keeps running. */
  hideBackgroundProgress(): void {
    this.backgroundProgressDismissed.set(true);
  }

  /** Hides the notification-area failure card. The organisation stays FAILED either way. */
  hideBackgroundFailure(): void {
    this.backgroundFailureDismissed.set(true);
  }

  /**
   * Opens the failure dialog from the card. The run this reports on may have failed in another
   * session entirely, so the organisation is marked as watched here — otherwise the effect above
   * reads the dialog as one nobody asked for and closes it on the next user refresh.
   */
  showFailure(): void {
    const orgId = this.auth.currentUser()?.organisationId;
    if (!orgId) return;
    this.dismissedOrgs.delete(orgId);
    this.activelyWatchedOrgs.add(orgId);
    this.internalWaitScreenVisible.set(true);
  }

  dismiss(): void {
    const user = this.auth.currentUser();
    if (user?.organisationId) {
      this.dismissedOrgs.add(user.organisationId);
    }
    this.backgroundProgressDismissed.set(false);
    // Closing the failure dialog is an answer to it; the card must not ask the same question again
    // the moment the overlay goes.
    this.backgroundFailureDismissed.set(this.onboardingStatus() === 'FAILED');
    this.internalWaitScreenVisible.set(false);
  }

  private refreshFailureCode(orgId: number): void {
    this.http
      .get<{ onboardingFailureCode: OnboardingFailureCode | null; domain: string | null }>(`${this.apiUrl}/organisation/${orgId}`)
      .pipe(catchError(() => of(null)))
      .subscribe(organisation => {
        if (!organisation) return;
        this.auth.currentUser.update(user => {
          if (!user?.organisation) return user;
          return {
            ...user,
            organisation: {
              ...user.organisation,
              onboardingFailureCode: organisation.onboardingFailureCode,
              domain: organisation.domain ?? user.organisation.domain,
            },
          };
        });
      });
  }

  private updateLocalOrgStatus(status: 'READY' | 'FAILED' | 'NONE'): void {
    this.auth.currentUser.update(user => {
      if (!user?.organisation) return user;
      return { ...user, organisation: { ...user.organisation, onboardingStatus: status } };
    });
    // `getUser()` replays one cached response for the life of the session, and AuthGuardService
    // calls `me()` on every navigation — so without this the next click writes the pre-import
    // snapshot back over the verdict we just learned, and the workspace flickers between states.
    this.auth.invalidateUserCache();
  }
}
